import { Signal } from 'typed-signals';
import cloneDeep from 'lodash/cloneDeep';
import type { TileColor } from '../../TileColor';
import MathUtils from '../../../../utils/MathUtils';
import { removeRandomElement } from '../../../../utils/array-utils';
import type { RefillMiddleware, RefillParams } from './RefillMiddleware';

export interface FreeSpaceMatchChanceConfig {
  freeSpaces: { min: number; max: number };
  matchChance: number;
  fullMatchChance: number;
}

export interface TrayRefillSettings {
  scoreRange: [number, number];
  availableColors: number;
  minColors: number;
  maxColors: number;
  minFill: number;
  maxFill: number;
  freeSpaceMatchChance?: FreeSpaceMatchChanceConfig[];
}

export class TrayRefillRandomizer {
  private refills: TrayRefillSettings[];

  private refillColors: TileColor[];

  private refill?: TrayRefillSettings;

  private refillMiddleware: RefillMiddleware | null = null;

  public onRefillSettingsChanged = new Signal<(settings: TrayRefillSettings) => void>();

  constructor(refills: TrayRefillSettings[], refillColors: TileColor[]) {
    this.refills = refills;
    this.refill = refills?.[0];
    this.refillColors = refillColors;
  }

  public updateProgress(progressRatio: number): void {
    const refill = this.refills.find(({ scoreRange }) =>
      MathUtils.isBetween(progressRatio, scoreRange[0], scoreRange[1]),
    );

    if (refill && refill !== this.refill) {
      this.refill = refill;
      this.onRefillSettingsChanged.emit(refill);
    }
  }

  public getTileColors(): TileColor[] {
    const colors: TileColor[] = [];

    const refillParams = this.getRefillParams();

    if (!refillParams) return colors;

    const { refillColors } = this;
    const { availableColors, minColors, maxColors, minFill, maxFill } = refillParams;

    const possibleFillColors = refillColors.slice(0, Math.min(availableColors, refillColors.length));
    const colorCount = Math.round(Math.max(MathUtils.getRandomValueInRange(minColors, maxColors), 0));
    if (colorCount === 0) return colors;

    for (let i = 0; i < colorCount; i += 1) {
      if (possibleFillColors.length === 0) break;
      const fillColor = removeRandomElement(possibleFillColors);
      const fillCount = MathUtils.getRandomValueInRange(minFill, maxFill);
      for (let j = 0; j < fillCount; j += 1) {
        colors.push(fillColor);
      }
    }
    return colors;
  }

  private getRefillParams(): RefillParams | null {
    if (!this.refill) return null;
    return this.refillMiddleware?.processRefill?.(cloneDeep(this.refill)) ?? this.refill;
  }

  public setMiddleware(middleware: RefillMiddleware | null): void {
    this.refillMiddleware = middleware;
  }

  public getCurrentRefillSettings(): TrayRefillSettings | undefined {
    return this.refill;
  }

  public setState(refills: TrayRefillSettings[], refillColors: TileColor[]): void {
    this.refills = refills;
    this.refill = refills?.[0];
    this.refillColors = refillColors;
  }
}
